import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  adminSystemConfigCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { useSessionStore } from "@/core/stores/session.store";
import { adminEndpoints } from "@/features/admin/api/endpoints";
import type {
  SystemConfigDto,
  UpdateSystemConfigRequestDto,
} from "@/features/admin/api/dto";

/**
 * Crée ou met à jour une clé de configuration (`PUT /SystemConfigs/{key}`).
 * L'auteur de la modification est tracé : `updatedByKeycloakId` = claim
 * `sub` de l'opérateur connecté. Rôles : flashpay-superadmin,
 * flashpay-admin (§6.8).
 */
export const useUpdateSystemConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["admin", "system-configs", "update"],
    mutationFn: ({
      key,
      value,
      description,
    }: {
      key: string;
      value: string;
      description?: string;
    }) => {
      const updatedByKeycloakId = useSessionStore.getState().userInfo?.sub;
      return mutateData({
        url: adminEndpoints.systemConfigs.update(key),
        reqBody: {
          value,
          description,
          updatedByKeycloakId:
            typeof updatedByKeycloakId === "string" ? updatedByKeycloakId : "",
        } satisfies UpdateSystemConfigRequestDto,
        method: "PUT",
      }) as Promise<SystemConfigDto>;
    },
    onSuccess: (_data, { key }) => {
      return invalidateAfterMutation(
        queryClient,
        adminSystemConfigCacheKeys(key)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdateSystemConfig failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "admin" }
      );
    },
  });
};
